import React, { useEffect, useState } from 'react';
import portalService from '../../services/portalService';
import { useLanguage, t } from '../../context/LanguageContext';

const SEVERITY_COLORS = { minor: '#ffc107', major: '#fd7e14', critical: '#dc3545' };

function MaskedValue({ value }) {
  const [visible, setVisible] = useState(false);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span className="fw-bold" style={{ fontSize: 20 }}>{visible ? value : '●●●●●'}</span>
      <button className="btn btn-link btn-sm p-0 text-muted" style={{ fontSize: 16 }} onClick={() => setVisible(v => !v)}>
        {visible ? '🙈' : '👁'}
      </button>
    </span>
  );
}

export default function PortalPointsPage() {
  const { lang } = useLanguage();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const CATEGORY_LABELS = {
    sanitation:           t('Sanitation', 'Saneamiento', lang),
    product_quality:      t('Product Quality', 'Calidad del producto', lang),
    waste:                t('Waste', 'Desperdicio', lang),
    customer_complaint:   t('Customer Complaint', 'Queja de cliente', lang),
    compliance:           t('Compliance', 'Cumplimiento', lang),
    other:                t('Other', 'Otro', lang),
    performance_target:   t('Performance Target', 'Objetivo de desempeño', lang),
  };

  const SEVERITY_LABELS = {
    minor:    t('minor', 'menor', lang),
    major:    t('major', 'mayor', lang),
    critical: t('critical', 'crítico', lang),
  };

  useEffect(() => {
    setLoading(true);
    portalService.getPoints(year).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [year]);

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;

  const pointColor = (p) => p >= 90 ? '#198754' : p >= 75 ? '#e6a817' : '#dc3545';
  const pct = data ? Math.round((data.currentPoints / data.startingPoints) * 100) : 0;

  const yearMarks = (data?.marks || []).filter(m => m.year === year);

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="fw-bold mb-0">{t('Points & Performance', 'Puntos y desempeño', lang)}</h5>
        <select className="form-select form-select-sm" style={{ width: 90 }} value={year} onChange={e => setYear(Number(e.target.value))}>
          {[currentYear, currentYear - 1, currentYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {!data ? <div className="text-muted text-center py-4" style={{ fontSize: 13 }}>{t('Could not load points.', 'No se pudieron cargar los puntos.', lang)}</div> : (
        <>
          {/* Points card */}
          <div className="card mb-3">
            <div className="card-body text-center py-4">
              <div style={{ fontSize: 13, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{t('Current Balance', 'Saldo actual', lang)}</div>
              <div style={{ fontSize: 52, fontWeight: 800, color: pointColor(data.currentPoints), lineHeight: 1 }}>
                {data.currentPoints}
              </div>
              <div style={{ fontSize: 14, color: '#6c757d' }}>{t('out of', 'de', lang)} {data.startingPoints} {t('points', 'puntos', lang)}</div>
              {data.deducted > 0 && <div style={{ fontSize: 12, color: '#dc3545', marginTop: 4 }}>−{data.deducted} {t('deducted this year', 'deducidos este año', lang)}</div>}
              <div className="progress mt-3" style={{ height: 8 }}>
                <div className="progress-bar" style={{ width: `${pct}%`, background: pointColor(data.currentPoints) }} />
              </div>
            </div>
          </div>

          {/* Bonus estimate */}
          {data.bonus && (
            <div className="card mb-3">
              <div className="card-body py-3">
                <div style={{ fontSize: 12, color: '#6c757d', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('Year-End Bonus Estimate', 'Estimado de bono de fin de año', lang)}</div>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div style={{ fontSize: 13 }}>{t('Bonus tier', 'Nivel de bono', lang)}: <strong>{data.bonus.pct}%</strong></div>
                    <div style={{ fontSize: 12, color: '#6c757d' }}>
                      {t('Target', 'Meta', lang)}: <MaskedValue value={`$${Number(data.bonus.target).toFixed(2)}`} />
                    </div>
                  </div>
                  <div className="text-end">
                    <div style={{ fontSize: 11, color: '#6c757d' }}>{t('Est. payout', 'Pago est.', lang)}</div>
                    <MaskedValue value={`$${Number(data.bonus.payout).toFixed(2)}`} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tier reference */}
          {data.tiers.length > 0 && (
            <details className="mb-3">
              <summary style={{ fontSize: 13, cursor: 'pointer', color: '#6c757d' }}>{t('Bonus tiers reference', 'Referencia de niveles de bono', lang)}</summary>
              <div className="card mt-2">
                <div className="card-body py-2 px-3">
                  <table className="table table-sm mb-0" style={{ fontSize: 12 }}>
                    <thead className="table-light">
                      <tr><th>{t('Min points', 'Puntos mín.', lang)}</th><th>{t('Bonus %', 'Bono %', lang)}</th></tr>
                    </thead>
                    <tbody>
                      {[...data.tiers].sort((a, b) => b.minPoints - a.minPoints).map((t2, i) => (
                        <tr key={i} style={{ fontWeight: data.currentPoints >= t2.minPoints && (i === 0 || data.currentPoints < data.tiers.sort((a,b)=>b.minPoints-a.minPoints)[i-1]?.minPoints) ? 700 : 400 }}>
                          <td>≥ {t2.minPoints} {t('pts', 'pts', lang)}</td>
                          <td>{t2.bonusPct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </details>
          )}

          {/* Mark history */}
          <h6 className="fw-semibold mb-2">{t('Mark History', 'Historial de notas', lang)} — {year}</h6>
          {yearMarks.length === 0 ? (
            <p className="text-muted" style={{ fontSize: 13 }}>{t('No marks this year. Keep it up!', 'Sin notas este año. ¡Siga así!', lang)}</p>
          ) : yearMarks.map(m => (
            <div key={m.id} className="card mb-2">
              <div className="card-body py-2 px-3">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <span className="fw-semibold" style={{ fontSize: 13 }}>{CATEGORY_LABELS[m.category] || m.category}</span>
                    <span className="ms-2 badge" style={{ fontSize: 10, background: SEVERITY_COLORS[m.severity], color: '#fff' }}>{SEVERITY_LABELS[m.severity] || m.severity}</span>
                    <div className="text-muted" style={{ fontSize: 12 }}>{m.date}{m.notes ? ` · ${m.notes}` : ''}</div>
                  </div>
                  <span style={{ fontWeight: 700, color: '#dc3545', fontSize: 14 }}>−{m.pointsDeducted}</span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
